import * as React from 'react'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, notification, Select, Skeleton, Switch, Radio } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { StatusBadge } from '@halo/components/badges/status-badge'
import { PromptReferrerAllowType } from '@halo/enums/prompt-referrer-allow-type'
import { isDef } from '@pushly/aqe/lib/utils/is-def'
import titleCase from 'title-case'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { legacyValidateFields } from '@halo/_utils/antd'
import { TypedTagSelect } from '@halo/components/typed-tag-select/typed-tag-select'
import cloneDeep from 'clone-deep'
import * as deepEqual from 'fast-deep-equal'
import { delay } from '@halo/_utils/delay'
import * as randomstring from 'randomstring'
import { DomainDto } from '@halo/dtos/domain.dto'
import {
    calculateToPromptFcapSeconds,
    getFcapAndReferrerConfigs,
    prepareGlobalPromptSettings,
} from '@halo/_utils/fcap-and-referrer-configs'
import { DomainService } from '@halo/services/domain.service'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { asCaslSubject } from '@halo/stores/ability.state'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { hydrateGlobalPromptReferrerConditions } from '@halo/_utils/models-hydration'

interface IDomainPromptEditor extends FormComponentProps {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDPEState {}

export const DomainPromptEditor = Form.create<IDomainPromptEditor>()(
    class extends React.Component<IDomainPromptEditor, IDPEState> {
        public state: IDPEState = {}

        protected appState: AppState
        protected appService: AppService
        protected domainService: DomainService

        public constructor(props: IDomainPromptEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.domainService = Container.get(DomainService)

            executeCleanDrawerRender(this)
        }

        public render() {
            const currReferrerConditions = hydrateGlobalPromptReferrerConditions(
                this.domain.global_prompt_settings?.referrers,
            )

            const currentValues = this.getCurrentValues()
            const fcapsEnabled = this.props.form.getFieldValue('fcapsEnabled') ?? !!this.domain.frequency_caps?.prompt
            const referrerConditionsEnabled =
                this.props.form.getFieldValue('referrerConditionsEnabled') ?? currReferrerConditions.enabled
            const config = getFcapAndReferrerConfigs(this.domain)

            return (
                <Drawer
                    className={classnames('ddf-prompt-settings-editor')}
                    title="Update Details"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form>
                        <Well title="Frequency Caps" mode="ghost" hideFooter={true}>
                            <Form.Item className="fcap-prompt-field" label="Prompt Frequency Cap">
                                <div className="fcap-prompt-row">
                                    <div className="fcap-prompt-left">
                                        {this.props.form.getFieldDecorator('fcapsEnabled', {
                                            initialValue: !!this.domain.frequency_caps?.prompt,
                                            valuePropName: 'checked',
                                        })(
                                            <Switch
                                                onChange={async () =>
                                                    delay(() => {
                                                        this.forceUpdate()
                                                    }, 0.05)
                                                }
                                            />,
                                        )}
                                    </div>
                                    <div className="fcap-prompt-right">
                                        {fcapsEnabled && (
                                            <>
                                                <span>A visitor to my site should be prompted</span>
                                                <div className="fcap-prompt-right-1">
                                                    {this.props.form.getFieldDecorator('promptFcapOccurrences', {
                                                        initialValue: config.defaultPromptFcapOccurrences,
                                                        rules: [
                                                            {
                                                                pattern: /^[\d]+$/i,
                                                                message: 'Occurrence count must be numeric',
                                                            },
                                                            {
                                                                validator: (rule, value, callback) => {
                                                                    value >= 1
                                                                        ? callback()
                                                                        : callback(new Error(rule.message))
                                                                },
                                                                message: 'Occurrence count must be 1 or greater',
                                                            },
                                                        ],
                                                    })(
                                                        <Input
                                                            size="small"
                                                            className="fcap-prompt-field-input"
                                                            placeholder="1"
                                                            autoComplete="off"
                                                            addonAfter="time(s) every"
                                                        />,
                                                    )}
                                                </div>
                                                <div className="fcap-prompt-right-2">
                                                    {this.props.form.getFieldDecorator('promptFcap', {
                                                        initialValue: config.defaultPromptFcapFrequency,
                                                        rules: [
                                                            {
                                                                pattern: /^[\d]+$/i,
                                                                message: 'Frequency must be numeric',
                                                            },
                                                            {
                                                                validator: (rule, value, callback) => {
                                                                    value >= 1 && value <= 30
                                                                        ? callback()
                                                                        : callback(new Error(rule.message))
                                                                },
                                                                message:
                                                                    'Frequency must be 1 or greater and less than or equal to 30',
                                                            },
                                                        ],
                                                    })(
                                                        <Input
                                                            size="small"
                                                            className="fcap-prompt-field-input"
                                                            placeholder="1"
                                                            autoComplete="off"
                                                            addonAfter={this.props.form.getFieldDecorator(
                                                                'promptFcapDisplayMetric',
                                                                {
                                                                    initialValue: config.defaultPromptFcapDisplayMetric,
                                                                },
                                                            )(
                                                                <Select className="fcap-prompt-metric-input">
                                                                    <Select.Option value="days">day(s)</Select.Option>
                                                                    <Select.Option value="hours">hour(s)</Select.Option>
                                                                    <Select.Option value="minutes">
                                                                        minute(s)
                                                                    </Select.Option>
                                                                </Select>,
                                                            )}
                                                        />,
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Form.Item>
                        </Well>

                        <Well title="Referrer Conditions" mode="ghost" hideFooter={true}>
                            <Form.Item className="referrer-field" label="Prompt Referrer Conditions">
                                <div className="referrer-field-row">
                                    {this.props.form.getFieldDecorator('referrerConditionsEnabled', {
                                        initialValue: currReferrerConditions.enabled,
                                        valuePropName: 'checked',
                                    })(
                                        <Switch
                                            onChange={async () =>
                                                delay(() => {
                                                    this.forceUpdate()
                                                }, 0.05)
                                            }
                                        />,
                                    )}
                                </div>
                                {referrerConditionsEnabled && (
                                    <React.Fragment>
                                        <div className="referrer-field-row collapse">
                                            <div className="referrer-field-left">
                                                <h4>When referrer is blank?</h4>
                                                {this.props.form.getFieldDecorator('referrerWhenBlank', {
                                                    initialValue: config.defaultReferrerWhenBlank,
                                                })(
                                                    <Radio.Group
                                                        size="small"
                                                        buttonStyle="solid"
                                                        className="referrer-when-blank-group"
                                                    >
                                                        <Radio.Button value={PromptReferrerAllowType.IGNORE}>
                                                            {PromptReferrerAllowType.IGNORE}
                                                        </Radio.Button>
                                                        <Radio.Button value={PromptReferrerAllowType.ALLOW}>
                                                            {PromptReferrerAllowType.ALLOW}
                                                        </Radio.Button>
                                                        <Radio.Button value={PromptReferrerAllowType.BLOCK}>
                                                            {PromptReferrerAllowType.BLOCK}
                                                        </Radio.Button>
                                                    </Radio.Group>,
                                                )}
                                            </div>
                                        </div>
                                        <div className="referrer-field-row">
                                            <div className="referrer-field-left">
                                                <h4>Allowed referrers:</h4>
                                                {this.props.form.getFieldDecorator('referrerAllow', {
                                                    initialValue: config.defaultReferrerAllowValue,
                                                })(
                                                    <TypedTagSelect
                                                        className="referrer-allow"
                                                        types={['exact', 'contains']}
                                                    />,
                                                )}
                                            </div>
                                            <div className="referrer-field-right">
                                                <h4>Blocked referrers:</h4>
                                                {this.props.form.getFieldDecorator('referrerBlock', {
                                                    initialValue: config.defaultReferrerBlockValue,
                                                })(
                                                    <TypedTagSelect
                                                        className="referrer-block"
                                                        types={['exact', 'contains']}
                                                    />,
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )}
                            </Form.Item>
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get domain(): DomainDto {
            return this.props.domain
        }

        protected getCurrentValues(): Partial<DomainDto> {
            let values: Partial<DomainDto> = {}
            const formValues = this.props.form.getFieldsValue()

            const fcapsEnabled = formValues.fcapsEnabled
            if (fcapsEnabled) {
                values.frequency_caps = {
                    prompt: {
                        occurrences: formValues.promptFcapOccurrences,
                        fcap_seconds: calculateToPromptFcapSeconds(formValues),
                        display_metric: formValues.promptFcapDisplayMetric,
                    },
                }
            } else {
                values.frequency_caps = null
            }
            if (deepEqual(values.frequency_caps, this.domain.frequency_caps)) {
                delete values.frequency_caps
            }

            const globalPromptSettings = prepareGlobalPromptSettings(
                formValues,
                cloneDeep(this.domain.global_prompt_settings),
            )
            if (!deepEqual(globalPromptSettings, this.domain.global_prompt_settings)) {
                values.global_prompt_settings = globalPromptSettings
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const values = this.getCurrentValues()

                const { ok, data } = await this.domainService.patch(this.domain.id, values, {
                    showLoadingScreen: true,
                })

                if (ok) {
                    this.props.onDomainUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }
    },
)

interface IDomainPromptWell {
    loading?: boolean
    domain: DomainDto
    title?: string
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

interface IDPWState {
    showDrawer: boolean
    drawerKey?: string
}

export class DomainPromptWell extends React.Component<IDomainPromptWell, IDPWState> {
    public state: IDPWState = {
        showDrawer: false,
    }

    protected readonly appState: AppState

    public constructor(props: IDomainPromptWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Prompt Settings"
                actions={
                    this.appState.abilityStore.can(
                        AbilityAction.UPDATE,
                        asCaslSubject(SubjectEntity.DOMAIN, {
                            ...this.props.domain,
                            statusId: this.props.domain?.status_id,
                        }),
                    ) && (
                        <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                            <EditOutlined />
                            <span>Edit</span>
                        </Button>
                    )
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderFrequencyCaps()}
                            {this.renderReferrerConditions()}

                            <DomainPromptEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                domain={this.domain}
                                onClose={this.handleDrawerClose}
                                onDomainUpdated={this.props.onDomainUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderFrequencyCaps() {
        const enabled = !!this.domain.frequency_caps?.prompt
        const config = getFcapAndReferrerConfigs(this.domain)

        return (
            <Well title="Frequency Caps" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>
                        <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                    </div>
                </div>

                {enabled && (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Prompt visitors to my site</div>
                            <div className={classnames('form-layout-row-value')}>
                                {config.defaultPromptFcapOccurrences} time(s) every {config.defaultPromptFcapFrequency}{' '}
                                {config.defaultPromptFcapDisplayMetric.replace(/s$/, '(s)')}
                            </div>
                        </div>
                    </>
                )}
            </Well>
        )
    }

    protected renderReferrerConditions() {
        const currReferrerConditions = hydrateGlobalPromptReferrerConditions(
            this.domain.global_prompt_settings?.referrers,
        )

        const enabled = currReferrerConditions.enabled
        const config = getFcapAndReferrerConfigs(this.domain)

        return (
            <Well title="Referrer Conditions" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>
                        <StatusBadge type="generic" expanded={true} status={enabled ? 'ENABLED' : 'DISABLED'} />
                    </div>
                </div>

                {enabled && (
                    <>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Blank Referrer Behavior</div>
                            <div className={classnames('form-layout-row-value')}>
                                {titleCase(config.defaultReferrerWhenBlank)}
                            </div>
                        </div>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Allowed Referrers</div>
                            <div className={classnames('form-layout-row-value')}>
                                {!isDef(config.defaultReferrerAllowValue)
                                    ? 'All Referrers'
                                    : config.defaultReferrerAllowValue.map((ref: any) => (
                                          <div key={ref.value}>
                                              <span>
                                                  {ref.type}: {ref.value}
                                              </span>
                                          </div>
                                      ))}
                            </div>
                        </div>
                        <div className={classnames('form-layout-row')}>
                            <div className={classnames('form-layout-row-label')}>Blocked Referrers</div>
                            <div className={classnames('form-layout-row-value')}>
                                {!isDef(config.defaultReferrerBlockValue)
                                    ? 'No Referrers'
                                    : config.defaultReferrerBlockValue.map((ref: any) => (
                                          <div key={ref.value}>
                                              <span>
                                                  {ref.type}: {ref.value}
                                              </span>
                                          </div>
                                      ))}
                            </div>
                        </div>
                    </>
                )}
            </Well>
        )
    }

    protected get domain(): DomainDto {
        return this.props.domain
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
